/**
 * client/src/lib/auth.tsx — who is asking, held in one place.
 *
 * One `GET /api/v1/session` at mount, awaited before anything that needs an actor (§6.1). The answer
 * is an `Actor` from `shared/actor.ts` — the same object `shared/access.ts` judges — so every screen
 * computes its rows from the predicate rather than from a tier string it inferred (LAW 13).
 *
 * ⚠️ **`loading` is a distinct state from "signed out", and it renders NEITHER face.** This is a
 * books.karti.ai scar quoted in its own AGENTS.md: rendering the door while the session request is
 * in flight flashes the wrong page at somebody who already has a key, on *every single load*. On `/`
 * it is the first paint of the site. `<Gate>` and `<Home>` in routes.tsx are the only two places
 * that read it, and there is deliberately no third.
 *
 * §6.1 also says the server answers a boot race with `no-session, retryable` rather than treating it
 * as terminal. The HTTP analogue is here: a *transport* failure retries once before we conclude
 * "signed out", because concluding it wrongly signs a person out of a year-long session on a subway.
 * An actual 4xx is an answer, and is not retried.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Actor } from "../../../shared/actor.ts";
import { isUser, userActor } from "../../../shared/actor.ts";
import { ApiError, abApi } from "./api.ts";
import type { SessionResponse, SessionUser } from "./api.ts";

interface AuthValue {
  /** The current actor: a signed-in `usr_`, a guest `gst_`, or null when the session is empty. */
  actor: Actor | null;
  /** The profile behind a `usr_` actor. Null for guests and for signed-out. */
  user: SessionUser | null;
  /** True until `/session` has answered. Renders neither face — see the header. */
  loading: boolean;
  /** True when the session request itself failed twice; the UI can offer "try again" instead of a door. */
  unreachable: boolean;
  signedIn: boolean;
  adopt: (res: SessionResponse) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

/**
 * A session payload can carry the actor, the user, or both. `actor` is authoritative when present;
 * when only `user` is, we build the actor with `userActor()` from `shared/actor.ts` rather than
 * hand-rolling the object — the `isAdmin` field in particular is derived live from
 * `users.email ∈ AB_ADMIN_EMAILS` per request (§6.1) and must arrive as the server computed it.
 */
function readSession(res: SessionResponse): { actor: Actor | null; user: SessionUser | null } {
  const user = res.user ?? null;
  if (res.actor) return { actor: res.actor, user };
  if (user) {
    return {
      actor: userActor({ id: user.id, email: user.email, handle: user.handle, isAdmin: user.isAdmin }),
      user,
    };
  }
  return { actor: null, user: null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [actor, setActor] = useState<Actor | null>(null);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await abApi.session();
      const next = readSession(res);
      setActor(next.actor);
      setUser(next.user);
      setUnreachable(false);
    } catch (err) {
      // A transport failure is not an answer. Retry once, then settle — see the header.
      if (err instanceof ApiError && err.offline) {
        await new Promise((r) => setTimeout(r, 700));
        try {
          const res = await abApi.session();
          const next = readSession(res);
          setActor(next.actor);
          setUser(next.user);
          setUnreachable(false);
          return;
        } catch {
          setActor(null);
          setUser(null);
          setUnreachable(true);
          return;
        }
      }
      setActor(null);
      setUser(null);
      setUnreachable(false);
    }
  }, []);

  useEffect(() => {
    let live = true;
    void load().finally(() => {
      if (live) setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [load]);

  const adopt = useCallback((res: SessionResponse) => {
    const next = readSession(res);
    setActor(next.actor);
    setUser(next.user);
    setUnreachable(false);
  }, []);

  const signOut = useCallback(async () => {
    // Clear locally even if the request fails. The cookie may already be gone, and leaving somebody
    // stuck "signed in" against a server that disagrees is worse than a stale row.
    await abApi.signOut().catch(() => {});
    /**
     * ⚠️ And clear what this device kept, which used to be everything.
     *
     * `CacheStorage` and IndexedDB survive a sign-out. On the shared family device this product is
     * built for, that meant: A signs in, opens a private book, downloads it, signs out — and B, with
     * the radio off, lands on the un-`<Gate>`d `/offline`, sees the title and the cover, and taps
     * through to the text and the audio. The `ab-clear-book-cache` message `sw.ts` implements had no
     * sender anywhere in the client.
     *
     * Imported dynamically for the reason `position.ts` gives at length: `test/opening.test.ts` and
     * the other Node-side suites import the auth module's siblings, and a static import of the IDB
     * layer would drag `caches`, `indexedDB` and `navigator.serviceWorker` into a `node --test`
     * process. `main.tsx` imports it statically, so at runtime this is already in the entry bundle.
     */
    await import("./idb.ts")
      .then((m) => m.clearPrivateOfflineData())
      .catch(() => {});
    setActor(null);
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const value = useMemo<AuthValue>(
    () => ({ actor, user, loading, unreachable, signedIn: isUser(actor), adopt, signOut, refresh }),
    [actor, user, loading, unreachable, adopt, signOut, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth() outside <AuthProvider>");
  return ctx;
}
